import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AUTH_CONFIG } from '../features/auth/auth.config';
import { StorageService } from './storage.service';

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
}

export interface TokenSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  accessExp: number | null; // ms epoch
  refreshExp: number | null; // ms epoch
}

@Injectable({ providedIn: 'root' })
export class TokenService {
  private storage = inject(StorageService);
  private cfg = inject(AUTH_CONFIG);

  readonly tokens$: Observable<TokenSnapshot>;

  private rawHttp = new HttpClient(inject(HttpBackend));

  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_EXPIRATION_KEY = 'token_expiration';
  private readonly REFRESH_TOKEN_EXPIRATION_KEY = 'refresh_token_expiration';

  private _tokens$: BehaviorSubject<TokenSnapshot>;

  constructor() {
    this._tokens$ = new BehaviorSubject<TokenSnapshot>(this.readSnapshot());
    this.tokens$ = this._tokens$.asObservable();
  }

  getAccessToken(): string | null {
    return this.storage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.storage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getAccessExp(): number | null {
    const v = this.storage.getItem(this.TOKEN_EXPIRATION_KEY);
    return v ? parseInt(v, 10) : null;
  }

  getRefreshExp(): number | null {
    const v = this.storage.getItem(this.REFRESH_TOKEN_EXPIRATION_KEY);
    return v ? parseInt(v, 10) : null;
  }

  refresh(): Observable<TokenResponse> {
    const { base, refresh } = this.cfg.endpoints.auth;
    return this.rawHttp.post<TokenResponse>(
      `${this.cfg.baseUrl}${base}${refresh}`,
      { refreshToken: this.getRefreshToken() },
      { headers: { Authorization: `Bearer ${this.getAccessToken()}` } },
    );
  }

  setTokens(resp: TokenResponse): void {
    const now = Date.now();
    const accessExp = now + resp.expires_in * 1000;
    const refreshExp = now + resp.refresh_expires_in * 1000;

    this.storage.setItem(this.TOKEN_KEY, resp.access_token);
    this.storage.setItem(this.TOKEN_EXPIRATION_KEY, String(accessExp));
    this.storage.setItem(this.REFRESH_TOKEN_KEY, resp.refresh_token);
    this.storage.setItem(this.REFRESH_TOKEN_EXPIRATION_KEY, String(refreshExp));

    this._tokens$.next(this.readSnapshot());
  }

  clear(): void {
    this.storage.removeItem(this.TOKEN_KEY);
    this.storage.removeItem(this.REFRESH_TOKEN_KEY);
    this.storage.removeItem(this.TOKEN_EXPIRATION_KEY);
    this.storage.removeItem(this.REFRESH_TOKEN_EXPIRATION_KEY);
    this._tokens$.next(this.readSnapshot());
  }

  private readSnapshot(): TokenSnapshot {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      accessExp: this.getAccessExp(),
      refreshExp: this.getRefreshExp(),
    };
  }
}
